import React, { Component } from 'react'

//#region Components

import DocumnetDetailsHeader from 'components/minutetaker/MinuteTakerHeader'
import MinutesDocumentDetail from 'components/minutetaker/MinutesDocumentDetail'

import FullscreenProgress from 'components/shared/FullscreenProgress'
import OfflineNotifier from 'components/shared/OfflineNotifier'
import DomHelper from 'components/helpers/DomHelper'
import selectors from '../../selectors/minuteTakerSelectors'
import ExportActionsDialog from 'components/minutetaker/components/ExportActionsDialog'

//#endregion

//#region Redux

import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { actions } from '../../reducers/minuteTakerReducer'
import { actions as editorActions } from '../../reducers/actionEditorReducer'
import { RouteComponentProps } from 'react-router-dom'
import { path, pathOr } from 'rambdax'
import { getMinutesDetail } from 'businesslayer/api/minutes'
import { getSections } from 'businesslayer/api/sections'
import { getActions } from 'businesslayer/api/actions'
import { DialogSubscriber } from 'components/shared/context/dialog'
import { EditorProvider } from 'components/contexts/editor'
import { LengthStats, calculateLength, pendoTrack, rounded } from 'common/util/functions'
import { pendoEvents } from 'common/util/pendoEvents'
import { Box } from '@material-ui/core'
import i18n from 'common/util/internationalization'
import LinearIndeterminate from 'components/shared/LinearProgress'

//#endregion

//#region Styles

const initialTeakerHeight = 640
const initialTeakerWidth = 1024

//#endregion

//#region Props
type ActionFn = (...args: any[]) => void

type Props = {
    actions: {
        fetchMinuteItemFulfilled: ActionFn
        loadActionsFulfilled: ActionFn
        saveExistingActionPending: ActionFn
        saveExistingActionRejected: ActionFn
        saveExistingActionFulfilled: ActionFn
        saveExistingAction: (params: any) => void
        loadSections: ActionFn
        resetCurrentMinuteItem: ActionFn
        resetActionEditorSession: ActionFn
        selectTab: ActionFn
        setCollapseAllVisibility: ActionFn
        clearSavedAction: ActionFn
    }
    onSetCommittee: (committeeId: string) => void
    minutesActions: ActionItem[] | null
    currentMinuteItem: Minutes
    committeeRole: UserRole
    currentCommittee: Committee
    history: any
} & RouteComponentProps

type State = {
    height: number
    width: number
    isLoading: boolean
    hasAccess: boolean
}

enum SelectedTab {
    Taker = 0,
    Actions = 1
}

//#endregion

//#region Implementation
//Note: that is umbrella component comprising others
class MinutesDetailPage extends Component<Props, State> {
    _isMounted = false
    // TODO: UI Refactor the layout. Layout should be declarative
    // and not need to keep track of dimensions in state.
    state = {
        height: initialTeakerHeight,
        width: initialTeakerWidth,
        isLoading: true,
        hasAccess: false
    }
    action: any

    async componentDidMount() {
        this._isMounted = true
        this.loadData()
    }
    componentDidUpdate() {
        const { committeeRole, currentMinuteItem, currentCommittee } = this.props
        const url: string = pathOr('', 'match.path', this.props)

        if (url.includes('/taker/:minutesId/actions')) {
            this.props.actions.setCollapseAllVisibility(false)
        } else {
            this.props.actions.setCollapseAllVisibility(true)
        }

        const documentCommitteeId: string = pathOr(
            '',
            ['relationships', 'committee', 'data', 'id'],
            currentMinuteItem
        )
        const documentStatus: DocumentStatusKey = pathOr(
            '',
            ['attributes', 'status'],
            currentMinuteItem
        )
        const isLoaded =
            !!currentCommittee.id &&
            !!documentCommitteeId &&
            currentCommittee.id === documentCommitteeId

        if (isLoaded) {
            const hasAccess = hasDocumentAccess(committeeRole, documentStatus)

            // Redirect to Landing Page if User does not have access
            if (!hasAccess) {
                this.handleRedirectToHome()
            }
            if (this.state.hasAccess !== hasAccess) {
                this.setState({ hasAccess })
            }
        }
    }
    componentWillUnmount() {
        this._isMounted = false
        //We have to reset current minute item and the list on unload to prevent the following glitch:
        //When user comes back to the taker, it renders old item first
        //(as reducer still has old current item).
        //Then reducer gets new item loaded in an async way and UI re-renders itself again for new data.
        //This makes visible artifact of some controls preserving older geometry (sizes).
        //So if we clean reducer upon exit, next time when we come back,
        //the render will happen in much better way.
        this.props.actions.resetCurrentMinuteItem()
        this.props.actions.resetActionEditorSession()
    }

    loadData = async (status?: string, prevStatus?: string) => {
        const minutesId: string = pathOr('-1', 'match.params.minutesId', this.props)

        try {
            const [minutesDetails, minutesSections, minutesActions] = await Promise.all([
                getMinutesDetail(minutesId, null, true),
                getSections(minutesId),
                getActions(minutesId)
            ])

            if (prevStatus === 'draft' && status === 'in_review') {
                let lengthObj = {}
                const {
                    avgCharCount,
                    avgWordCount,
                    minCharCount,
                    minWordCount,
                    maxCharCount,
                    maxWordCount
                }: LengthStats = calculateLength(minutesSections)
                lengthObj = {
                    AVG_WORD_COUNT: avgWordCount,
                    AVG_CHARACTER_COUNT: avgCharCount,
                    MIN_WORD_COUNT: minWordCount,
                    MIN_CHARACTER_COUNT: minCharCount,
                    MAX_CHARACTER_COUNT: rounded(maxCharCount),
                    MAX_WORD_COUNT: maxWordCount
                }

                pendoTrack(pendoEvents.SECTIONDETAILS, lengthObj)
            }

            // Get committee id of current minutes document and set that as the current committee id
            // in order to prevent errors while we refactor endpoints to not include committee ids any more
            if (!!minutesDetails?.committees) {
                const committeeId = Object.keys(minutesDetails.committees)[0]
                this.props.onSetCommittee(committeeId)
            }

            if (this._isMounted) {
                this.props.actions.fetchMinuteItemFulfilled(minutesDetails)
                this.props.actions.loadSections(minutesSections)
                this.props.actions.clearSavedAction()
                this.props.actions.loadActionsFulfilled(minutesActions)

                setTimeout(() => {
                    if (!!this._isMounted) {
                        this.setState({ isLoading: false })
                    }
                }, 500)
            }
        } catch (error) {
            if (this._isMounted) {
                this.setState({ isLoading: false })
            }
        }
    }

    handleStatusChange = (status?: string, prevStatus?: string) => {
        this.loadData(status, prevStatus)
    }

    private async reloadActions() {
        const minutesId: string = pathOr('-1', 'match.params.minutesId', this.props)

        try {
            const data = await getActions(minutesId)
            this.props.actions.loadActionsFulfilled(data)
        } catch (error) {
            console.log('Error in reloadActions:', error)
        }
    }

    private async saveActionAndReload(actionWithRequiredFields) {
        try {
            this.props.actions.saveExistingActionPending()

            const { saveAction } = await import('businesslayer/api/actions')
            const result = await saveAction(
                actionWithRequiredFields,
                null,
                actionWithRequiredFields.isAdmin
            )

            const structuredData = {
                actionItems: {
                    [actionWithRequiredFields.id]: result.actionItems[actionWithRequiredFields.id]
                }
            }
            this.props.actions.saveExistingActionFulfilled(structuredData)

            await this.reloadActions()
        } catch (error) {
            console.log('Update API failed:', error)
            this.props.actions.saveExistingActionRejected(error)
        }
    }

    private handleActionChecked = (actionItem: ActionItem) => {
        if (actionItem.completionStatus === 'complete') {
            actionItem.completionStatus = 'incomplete'
        } else {
            actionItem.completionStatus = 'complete'
        }

        this.props.actions.saveExistingActionPending()

        // Add committee_id and isAdmin flag to the actionItem
        // ActionItem doesn't have committee_id but saveAction requires it
        const committeeId = pathOr(
            '',
            ['relationships', 'committee', 'data', 'id'],
            this.props.currentMinuteItem
        )
        const isAdmin = this.props.committeeRole === 'ADMIN'
        const actionWithRequiredFields = {
            ...actionItem,
            committee_id: committeeId,
            isAdmin
        }

        this.saveActionAndReload(actionWithRequiredFields)
    }

    private handleRedirectToHome = () => {
        this.props.actions.resetActionEditorSession()
        const { history } = this.props
        const currentMinutesId = path<string>('match.params.minutesId', this.props)

        // Check if we're on the action dashboard page - don't redirect in that case
        const currentPath = window.location.pathname
        if (currentPath === '/actionDashboard') {
            return
        }

        history.push({ pathname: '/', state: { currentMinutesId } })
    }

    private onResize = (evt) => {
        this.setState({ height: evt.height, width: evt.width })
    }

    private renderLoading = () => {
        return <LinearIndeterminate loading={this.state.isLoading} />
    }

    private setIsLoading = (isLoading) => {
        this.setState({ isLoading: isLoading })
    }

    render() {
        const { minutesActions } = this.props

        const normalizewdWidth = Math.max(this.state.width, 800)
        const selectedTabIndex = determineSelectedTab(this.props)

        return (
            <EditorProvider>
                <React.Fragment>
                    <Box style={{ flex: 'auto' }}>
                        <Box>
                            <DocumnetDetailsHeader
                                width={normalizewdWidth}
                                title={i18n.t('TOOLTIP_MINUTES')}
                            />
                            <Box>
                                {this.renderLoading()}
                                <Box>
                                    {this.state.hasAccess ? (
                                        <MinutesDocumentDetail
                                            minutesActions={minutesActions || []}
                                            onActionChecked={this.handleActionChecked}
                                            selectedTab={selectedTabIndex}
                                            currentMinuteItem={this.props.currentMinuteItem}
                                            onStatusChange={this.handleStatusChange}
                                            reloadActions={this.reloadActions.bind(this)}
                                            setIsLoading={this.setIsLoading}
                                        />
                                    ) : null}
                                </Box>
                            </Box>

                            <FullscreenProgress />
                            <DomHelper onResize={this.onResize} />
                            <OfflineNotifier />
                        </Box>
                    </Box>
                    <DialogSubscriber name="ExportActionsDialog">
                        {({ isDialogShown }) => {
                            return isDialogShown ? <ExportActionsDialog /> : null
                        }}
                    </DialogSubscriber>
                </React.Fragment>
            </EditorProvider>
        )
    }
}

const hasDocumentAccess = (committeeRole: UserRole, documentStatus: DocumentStatusKey) => {
    if (committeeRole === 'ADMIN') return true
    if (committeeRole === 'DIRECTOR' && documentStatus === 'in_review') return true

    return false
}

// Tooltips in ckeditor are 1000 and sit above navbar

//#endregion

//#region Export / Redux Bindings
const mapStateToProps = (state, _) => {
    return {
        minutesActions: selectors.minutesActions(state.minuteTakerReducer),
        selectedTabIndex: selectors.selectedTabIndex(state.minuteTakerReducer),
        currentMinuteItem: selectors.currentMinuteItem(state.minuteTakerReducer)
    }
}

const mapDispatchToProps = (dispatch) => {
    const {
        fetchMinuteItemFulfilled,
        resetCurrentMinuteItem,
        resetActionEditorSession,
        setCollapseAllVisibility,
        selectTab,
        loadActionsFulfilled,
        saveExistingActionPending,
        saveExistingActionRejected,
        saveExistingActionFulfilled,
        saveExistingAction,
        loadSections,
        clearSavedAction
    } = { ...actions, ...editorActions }

    return {
        actions: bindActionCreators(
            {
                fetchMinuteItemFulfilled,
                resetCurrentMinuteItem,
                resetActionEditorSession,
                setCollapseAllVisibility,
                loadActionsFulfilled,
                saveExistingActionPending,
                saveExistingActionRejected,
                saveExistingActionFulfilled,
                saveExistingAction,
                loadSections,
                selectTab,
                clearSavedAction
            },
            dispatch
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MinutesDetailPage)

//#endregion

const determineSelectedTab = (props) =>
    path('match.path', props) === '/taker/:minutesId'
        ? // || path('match.path', props) === '/review/:minutesId' ||
          // path('match.path', props) === '/final-draft/:minutesId' ||
          // path('match.path', props) === '/approved/:minutesId'
          SelectedTab.Taker
        : SelectedTab.Actions
